import { useState } from "react";
import { Mail, Check, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

export default function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    setMsg("");
    try {
      const { data } = await api.post("/marketing/newsletter/subscribe", {
        email: email.trim().toLowerCase(),
        source: "footer",
      });
      if (data?.ok) {
        setState("success");
        setMsg(data.message || "Thanks — you're on the list.");
        setEmail("");
      } else {
        setState("error");
        setMsg(data?.message || "Couldn't subscribe — try again.");
      }
    } catch (err) {
      setState("error");
      setMsg(formatApiError(err) || "Couldn't subscribe — try again.");
    }
  };

  return (
    <section className="bg-[#0F172A] text-white" data-testid="newsletter-signup">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold tracking-[0.18em] uppercase text-[#F26953]">
              <Mail className="h-3 w-3" /> Free weekly
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-3">
              One short email a week. Worth opening.
            </h2>
            <p className="text-sm md:text-base text-slate-300 mt-2 max-w-xl">
              Domain + email tips for small businesses — what works, what scams to avoid, and how to keep your customers reading instead of marking you as spam. No fluff. Unsubscribe in one click.
            </p>
          </div>
          <form onSubmit={submit} className="w-full md:w-auto" data-testid="newsletter-form">
            {state === "success" ? (
              <div
                data-testid="newsletter-success"
                className="inline-flex items-center gap-2 rounded-xl bg-emerald-500/15 border border-emerald-400/30 px-4 py-3 text-sm text-emerald-200"
              >
                <Check className="h-4 w-4" />
                {msg}
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-[420px]">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="newsletter-email-input"
                  className="flex-1 rounded-lg px-4 py-3 text-sm bg-white text-[#0F172A] placeholder-slate-400 outline-none focus:ring-2 focus:ring-[#F26953]/40"
                />
                <button
                  type="submit"
                  disabled={state === "submitting"}
                  data-testid="newsletter-submit-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#F26953] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 transition-colors text-sm"
                >
                  {state === "submitting" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Subscribing…
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </button>
              </div>
            )}
            {state === "error" && (
              <p data-testid="newsletter-error" className="text-xs text-red-300 mt-2">
                {msg}
              </p>
            )}
            {state !== "success" && (
              <p className="text-[11px] text-slate-400 mt-2 max-w-md">
                We send one short email a week. Your address stays private — no selling, no spam.
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
